import {Injectable, isDevMode, NgZone, Injector} from '@angular/core';
import {Router} from '@angular/router';
import {AppInsightsService} from '@markpieszak/ng-application-insights';
import { HttpErrorResponse } from '@angular/common/http';
import forIn from 'lodash-es/forIn';
import {Observable, Subject, throwError, EMPTY, BehaviorSubject, from} from 'rxjs';
import {ProfileService} from '../profile/profile.service';
import {WindowService} from '../window/window.service';
import {VideoPortalUser} from './video-portal-user.model';
import {UserInfo} from "./video-portal-user.model";
import {BroadcastService, MsalService} from '@azure/msal-angular';
import {Logger, Account, AuthError, AuthResponse, InteractionRequiredAuthError} from 'msal';
import {take, catchError, shareReplay, tap, finalize} from 'rxjs/operators';
import {AuthConfigService} from '../auth-config/auth-config.service';
import {ReturnUrlService} from '../return-url/return-url.service';
import { SpinnerService } from '../spinner/spinner.service';

@Injectable()
export class AuthService {
    msalInitPromise: Promise<void> | null = null;

    private _userInfoSubject: BehaviorSubject<VideoPortalUser | null> = new BehaviorSubject<VideoPortalUser | null>(null);
    private _loggedIn = false;
    private authRedirectCount = 0;
    private lastRedirectTime = 0;
    private readonly MAX_REDIRECTS = 3;
    private readonly REDIRECT_WINDOW_MS = 10000; // 10 seconds
    private tokenAcquisitionInProgress = false;
    private currentTokenPromise: Promise<any> | null = null;
    private _loginSuccessInProgress = false;
    private msalInitialized = false;
    private loginInProgress = false;

    constructor(
        private windowService: WindowService,
        private broadcastService: BroadcastService,
        private msalService: MsalService,
        private zone: NgZone,
        private authConfigService: AuthConfigService,
        private injector: Injector,
        private spinnerService: SpinnerService
    ) {
        // Check for login in progress from previous session
        this.loginInProgress = sessionStorage.getItem('login_in_progress') === 'true';
        if (this.loginInProgress) {
            // Clear after a delay to prevent permanent lockout
            setTimeout(() => {
                this.loginInProgress = false;
                sessionStorage.removeItem('login_in_progress');
            }, 5000);
        }

        // Register the redirect callback immediately in the constructor
        this.registerRedirectCallback();
    }

    async initMSAL(): Promise<void> {
        if (this.msalInitialized) {
            if (isDevMode()) {
                console.log('MSAL already initialized, returning existing promise.');
            }
            // Return the existing promise OR a resolved one if somehow null
            return this.msalInitPromise || Promise.resolve();
        }

        // Create initialization promise to prevent race conditions
        if (!this.msalInitPromise) {
            this.msalInitPromise = new Promise<void>(async (resolve) => { // Make inner function async
                if (isDevMode()) {
                    console.log('Initializing MSAL');
                }

                // Set up broadcast listeners before checking account to ensure we catch all events
                this.setupBroadcastListeners();

                // Set up logger
                this.msalService.setLogger(new Logger((logLevel, message, piiEnabled) => {
                    if (isDevMode()) {
                        console.log('MSAL Logging: ', message);
                    }
                }, {
                    piiLoggingEnabled: false,
                }));

                // Check account status -- MOVED TO handleLoginSuccess AND post-init check below

                this.msalInitialized = true;
                resolve(); // Resolve after basic setup is complete
            });
        }

        // Wait for the basic initialization promise to resolve
        await this.msalInitPromise;

        // === NEW LOGIC: Check account AFTER basic MSAL init completes ===
        if (isDevMode()) {
            console.log('MSAL init complete. Checking for existing account.');
        }
        // Ensure we are not in the middle of a redirect flow by checking if MSAL has a hash to process
        const isProcessingRedirect = this.windowService.window.location.hash.includes('#code=') || this.windowService.window.location.hash.includes('#error=');

        if (!isProcessingRedirect) {
            const account = this.msalService.getAccount();
            // Check if account exists AND we are not already logged in (or login success isn't already processing)
            if (account && !this.isLoggedIn && !this._loginSuccessInProgress) {
                if (isDevMode()) {
                    console.log('Existing account found on init/refresh, attempting to restore session.', account);
                }
                // Use zone.run to ensure Angular change detection picks it up
                this.zone.run(() => {
                    // Treat this like a login success scenario, but without a specific AuthResponse.
                    // Call handleLoginSuccess, ensuring the check inside it prevents double execution.
                   this.handleLoginSuccess(null); // Pass null to indicate session restore
                });
            } else if (!account) {
                 if (isDevMode()) {
                     console.log('No existing account found on init/refresh.');
                 }
            } else if (this._loginSuccessInProgress) {
                 if (isDevMode()) {
                     console.warn("Login success already processing during init check, skipping session restore attempt.");
                 }
            }
        } else {
             if (isDevMode()) {
                 console.log('MSAL redirect hash detected in URL, skipping initial account check (expecting broadcast).');
             }
        }
        // =============================================================

        // Return the original promise after the check is initiated
        return this.msalInitPromise;
    }

    login(): void {
        // Ensure MSAL is initialized first
        this.initMSAL().then(() => {
            // Don't attempt login if one is already in progress
            if (this.loginInProgress) {
                console.log('Login already in progress, ignoring duplicate request');
                return;
            }

            const now = Date.now();

            // Reset counter if enough time has passed
            if (now - this.lastRedirectTime > this.REDIRECT_WINDOW_MS) {
                this.authRedirectCount = 0;
            }

            this.authRedirectCount++;
            this.lastRedirectTime = now;

            if (this.authRedirectCount > this.MAX_REDIRECTS) {
                console.error('Auth redirect loop detected - breaking cycle');
                // Get Router via injector
                const router = this.injector.get(Router);
                router.navigate(['error'], {queryParams: {reason: 'auth_loop'}});
                return;
            }

            // Reset login success flag before starting redirect
            this._loginSuccessInProgress = false;

            // Set flag before redirecting
            this.loginInProgress = true;

            // Store flag in sessionStorage to persist across redirects
            sessionStorage.setItem('login_in_progress', 'true');

            // Add timeout to reset flag in case redirect fails
            setTimeout(() => {
                this.loginInProgress = false;
                sessionStorage.removeItem('login_in_progress');
            }, 30000); // 30 second timeout

            this.msalService.loginRedirect();
        });
    }

    logout(): void {
        console.log("Calling msalService.logout()...");
        // Clear local state
        this._loggedIn = false;
        this._userInfoSubject.next(null);
        // Clear app insights context
        const ai = this.injector.get(AppInsightsService);
        ai.clearAuthenticatedUserContext();
        // Reset flags on logout
        this._loginSuccessInProgress = false;
        // Clear local storage, including returnUrl
        this.clearLocalStorage();
        // Let MSAL handle the actual logout
        this.msalService.logout();
    }

    checkoutAccount(): void {
        const account: Account | null = this.msalService.getAccount();
        this._loggedIn = !!account;

        if (isDevMode()) {
            console.log('this._loggedIn: ' + this._loggedIn);
        }
    }

    getBearerToken(resource: string[]): Observable<any> {
        // Use the async/await pattern consistently inside the from() conversion
        return from((async () => {
            await this.initMSAL();

            // If we already have a token acquisition in progress, return that promise
            if (this.tokenAcquisitionInProgress && this.currentTokenPromise) {
                return this.currentTokenPromise;
            }

            this.tokenAcquisitionInProgress = true;
            const userAccount: Account | null = this.msalService.getAccount();

            if (!userAccount) {
                this.tokenAcquisitionInProgress = false;
                throw new Error('User account not found for acquiring token silently.');
            }

            try {
                // Create a new promise for token acquisition with proper cleanup
                const response = await this.msalService.acquireTokenSilent({
                    scopes: resource,
                });

                if (isDevMode()) {
                    console.log('Acquire token silent completed.');
                }
                this.windowService.localStorage.setItem("msal.access_token", response.accessToken);

                this.tokenAcquisitionInProgress = false;
                return response;
            } catch (error) {
                this.tokenAcquisitionInProgress = false;
                this.currentTokenPromise = null;

                // Check if interaction is required (common for B2B guest accounts)
                const isInteractionRequired =
                    (error && error.name === 'InteractionRequiredAuthError') ||
                    (error && error.errorCode === 'interaction_required') ||
                    (error && error.errorCode === 'login_required') ||
                    (error && error.errorCode === 'consent_required');

                if (isInteractionRequired) {
                    if (isDevMode()) {
                        console.warn('Interaction required during silent token acquisition. Triggering interactive login...', error);
                    }
                    // Interactive recovery for guest accounts - redirect to login
                    this.msalService.loginRedirect({ scopes: resource });
                    return Promise.reject(error); // redirect navigates away
                }

                throw error;
            }
        })()).pipe(
            // Share the observable to prevent multiple token requests for the same resource
            shareReplay(1)
        );
    }

    handleCallback(): void {
        this.initMSAL().then(() => {
            this.msalService.loginRedirect();
        });
    }

    async refresh(): Promise<void> {
        console.log("AuthService: Refreshing profile...");
        try {
            // Get ProfileService via injector
            const profileService = this.injector.get(ProfileService);
            await profileService.refresh(); // Ensure profile source is updated
            // Re-fetch profile, create user, and emit on subject
            await this.fetchProfileAndEmitUser();
        } catch (error) {
            if (isDevMode()) {
                console.error("AuthService: Error during refresh:", error);
            }
            // Get Router via injector
            const router = this.injector.get(Router);
            router.navigate(['/error'], {queryParams: {reason: 'refresh_failed'}});
        }
    }

    clearLocalStorage(): void {
        forIn(this.windowService.localStorage, (value: string, key: string) => {
            if (key.startsWith('msal.') || key.startsWith('{\"authority\":') || key.startsWith('returnUrl')) {
                this.windowService.localStorage.removeItem(key);
            }
        });
    }

    async ensureValidToken(): Promise<void> {
        await this.initMSAL();

        const account = this.msalService.getAccount();

        if (!account) {
            if (isDevMode()) {
                console.log('No account found - redirecting to login');
            }
            this.login();
            return;
        }

        // Check if we have a valid token
        const resource = [this.windowService.localStorage.getItem('msal.resource') || ''];
        if (!resource[0]) {
            return; // No resource to validate
        }

        try {
            await this.getBearerToken(resource).toPromise();
            this._loggedIn = true;
        } catch (error) {
            if (isDevMode()) {
                console.error('Token validation failed:', error);
            }
            this.clearLocalStorage();
            this.login();
        }
    }

    get isLoggedIn(): boolean {
        return this._loggedIn;
    }

    get userInfo(): VideoPortalUser | null {
        return this._userInfoSubject.getValue();
    }

    get userInfoSubject(): Observable<VideoPortalUser | null> {
        return this._userInfoSubject.asObservable();
    }

    private setupBroadcastListeners(): void {
        // Use a more robust approach to handle broadcast events within NgZone
        this.broadcastService.subscribe('msal:loginSuccess', (payload) => {
            // Call handleLoginSuccess when MSAL signals success via broadcast
            this.zone.run(() => this.handleLoginSuccess(payload as AuthResponse));
        });

        this.broadcastService.subscribe('msal:loginFailure', (error) => {
            this.zone.run(() => {
                this.loginInProgress = false;
                sessionStorage.removeItem('login_in_progress');
                if (isDevMode()) {
                    console.error('Login Failure:', error);
                }
                // Clean up any existing token state on login failure
                this.clearLocalStorage();
                // Try to redirect to login again
                this.login();
            });
        });

        this.broadcastService.subscribe('msal:acquireTokenFailure', (error) => {
            this.zone.run(() => {
                if (isDevMode()) {
                    console.error('Token Acquisition Failure:', error);
                }

                if (error && (error.errorCode === 'AADSTS160021' || error.errorCode === 'AADSTS65001')) {
                    console.warn('Forcing login due to critical acquire token failure:', error.errorCode);
                    this._loggedIn = false;
                    this.clearLocalStorage();
                    this.login();
                } else if (error && error.name === 'InteractionRequiredAuthError') {
                    console.warn('Interaction required during silent token acquisition.', error);
                }
            });
        });
    }

    private handleLoginSuccess(response?: AuthResponse | null): void {
        // Prevent double execution of this logic
        if (this._loginSuccessInProgress) {
            if (isDevMode()) {
                console.log('handleLoginSuccess already in progress, ignoring duplicate call.', response);
            }
            return;
        }
        this._loginSuccessInProgress = true;
        const isSessionRestore = response === null; // Determine if this is a restore
        if (isDevMode()) {
            console.log(`handleLoginSuccess started. Is session restore: ${isSessionRestore}`, response);
        }

        // Clear login in progress flag from storage and memory *before* checking account
        sessionStorage.removeItem('login_in_progress');
        this.loginInProgress = false;

        // Check/Update account info *after* successful redirect processing OR on session restore
        this.checkoutAccount(); // Updates _loggedIn based on msalService.getAccount()

        if (this._loggedIn) {
            if (isDevMode()) {
                console.log('User is considered logged in, proceeding to fetch profile.');
            }
            // Show spinner before starting profile fetch
            this.spinnerService.start();

            // Always fetch a fresh profile after login success
            // Chain the flag reset after the promise settles
            const cleanup = () => {
                this._loginSuccessInProgress = false; // Reset flag AFTER profile fetch completes or fails
                this.spinnerService.stop();
                if (isDevMode()) {
                    console.log('handleLoginSuccess cleanup executed.');
                }
            };

            // Pass the restore flag to the profile fetch function
            this.fetchProfileAndEmitUser(isSessionRestore).then(
                () => { // Success case
                    cleanup();
                     if (isDevMode()) {
                        console.log('Profile fetched and user emitted successfully after login/session restore.');
                    }
                },
                (err) => { // Error case
                     console.error("Error during fetchProfileAndEmitUser called from handleLoginSuccess:", err);
                     cleanup(); // Run cleanup even on error
                 }
            );
        } else {
            // If not logged in (e.g., checkoutAccount failed), reset the flags immediately
            this._loginSuccessInProgress = false;
             if (isDevMode()) {
                console.warn("handleLoginSuccess called, but user is NOT considered logged in after checkoutAccount().");
            }
            // Stop spinner if it was started
            this.spinnerService.stop();
        }
    }

    private async fetchProfileAndEmitUser(isSessionRestore: boolean = false): Promise<void> {
        const callId = Math.random().toString(36).substring(2, 8);
        const account: Account | null = this.msalService.getAccount();
        if (!account) {
            throw new Error("MSAL account not found during profile fetch.");
        }

        if (!account.idTokenClaims || !account.userName) {
            this.clearLocalStorage();
            this.login();
            throw new Error("Invalid token claims");
        }

        const ui: UserInfo = {
            userName: account.userName,
            profile: account.idTokenClaims
        };

        return new Promise<void>((resolve, reject) => {
            // Get ProfileService via injector
            const profileService = this.injector.get(ProfileService);
            profileService.profile.pipe(
                take(1),
                catchError(error => {
                    if (error instanceof HttpErrorResponse && error.status === 401) {
                        this.loginInProgress = false;
                        sessionStorage.removeItem('login_in_progress');
                        this.login();
                        return EMPTY;
                    }

                    // Get Router via injector
                    const router = this.injector.get(Router);
                    router.navigate(['/error'], { queryParams: { reason: 'profile_fetch_failed' } });
                    return throwError(error);
                })
            ).subscribe({
                next: profile => {
                    if (profile) {
                        try {
                            const userInfo = new VideoPortalUser(ui, profile);
                            this.zone.run(() => {
                                this._userInfoSubject.next(userInfo);
                                // Get AppInsightsService via injector
                                const ai = this.injector.get(AppInsightsService);
                                ai.setAuthenticatedUserContext(userInfo.username, userInfo.councilName, true);
                                if (!userInfo.hasAgreedToTermsAndConditions) {
                                    // Get Router via injector
                                    const router = this.injector.get(Router);
                                    if (router.url !== '/terms-and-conditions') {
                                        router.navigate(['/terms-and-conditions']);
                                    }
                                } else {
                                    // Only navigate using ReturnUrlService if this is NOT a session restore
                                    if (!isSessionRestore) {
                                        if (isDevMode()) {
                                            console.log('Login success (not restore): Clearing return URL and navigating.');
                                        }
                                        // Get ReturnUrlService via injector
                                        const returnUrlService = this.injector.get(ReturnUrlService);
                                        returnUrlService.clear(); // Clear the URL
                                        returnUrlService.navigate(); // Navigate (will default to home if URL was cleared)
                                    } else {
                                        if (isDevMode()) {
                                            console.log('Session restore: Skipping navigation via ReturnUrlService.');
                                        }
                                    }
                                }
                            });
                            resolve();
                        } catch (error) {
                            console.error("Error creating user info:", error);
                            reject(error);
                        }
                    } else {
                        console.warn("Profile fetch did not return a profile.");
                        resolve();
                    }
                },
                error: err => {
                    console.error("Profile fetch subscription received error:", err);
                    reject(err);
                }
            });
        });
    }

    // New private method to encapsulate callback registration
    private async registerRedirectCallback(): Promise<void> {
        this.msalService.handleRedirectCallback((authError: AuthError | null, response: AuthResponse | null) => {
            // No longer need the _redirectProcessed flag check here
            this.zone.run(() => {
                // Let MSAL process the hash, but DO NOT call handleLoginSuccess from here.
                // Rely on the 'msal:loginSuccess' broadcast instead.
                if (authError) {
                    console.error('Redirect Error: ', authError.errorMessage);
                    // Handle specific errors if needed
                } else if (response) {
                    if (isDevMode()) {
                        // Log success but don't act on it here
                        console.log('Redirect Success via handleRedirectCallback (Processing ONLY, logic triggered by broadcast).', response);
                    }
                }
            });
        });
    }

    get isLoginInProgress(): boolean {
        return this.loginInProgress;
    }

    // Getter for the initialization promise
    get initializationPromise(): Promise<void> | null {
        return this.msalInitPromise;
    }

    // Getter for login success status
    get isLoginSuccessInProgress(): boolean {
        return this._loginSuccessInProgress;
    }
}
